import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { BTN_XS } from '../../constants/buttons';
import { TASK_STATUS_COLORS_HEX, TASK_STATUS_LABELS, TASK_STATUS_ICONS, TASK_STATUS_ICON_COLORS } from '../../constants/taskStatuses';
import { formatDate } from '../../utils/dateFormat';
import { createPortal } from 'react-dom';
import useDragScroll from '../../hooks/useDragScroll';
import useGanttScrollFrame from '../../hooks/useGanttScrollFrame';
import useGanttZoom from '../../hooks/useGanttZoom';
import GanttBarTooltip, { useGanttBarTooltip } from '../gantt/GanttBarTooltip';
import GanttDragDatesTooltip, { GANTT_DRAGGING_ATTR } from '../gantt/GanttDragDatesTooltip';
import GanttDateAxis, { GANTT_AXIS_Z, GANTT_DRAG_Z, GANTT_BAR_HEIGHT, GanttGrid, GanttTodayLine } from '../gantt/GanttDateAxis';
import GanttZoomControl from '../gantt/GanttZoomControl';
import StickyScrollbar from '../ui/StickyScrollbar';
import { PAGE_SCROLL_PANE_X } from '../../constants/stickyTable';
import { parseDate, daysDiff, addDays, toDateStr, buildDateHeaders } from '../../utils/ganttDates';
import { MODAL_OVERLAY, MODAL_PANEL_MOBILE_CAP } from '../../constants/overlayLayers';
import { GANTT_CURSOR } from '../../constants/ganttCursors';
import { useGanttLiveDrags, usePublishGanttLiveDrag, ganttTaskLiveKey } from '../gantt/ganttLiveDrag';

const TEAM_COLORS = [
  { hex: '#3B82F6' }, { hex: '#10B981' }, { hex: '#8B5CF6' }, { hex: '#F59E0B' },
  { hex: '#F43F5E' }, { hex: '#06B6D4' }, { hex: '#F97316' }, { hex: '#6366F1' },
];

const formatShort = (str) => { if (!str) return ''; const d = new Date(str + 'T00:00:00'); return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }); };

// Assignee summary for the Gantt (bar tooltip + left-label tooltip + inline
// bar chip). Guarded by the caller's showAssignees prop — teams callers don't
// reliably populate assignees (my-tasks hardcodes []), so they opt OUT and
// this is never called for them. Reads task.assignees [{ display_name }] (the
// gag-detail shape). tooltipLine: "Assigned: A, B" (up to two, then "+N"),
// or "Not assigned". inline: primary name + "+N" for the rest, or null when
// unassigned (never shown inline — too noisy on short bars).
function assigneeInfo(task) {
  const names = Array.isArray(task.assignees)
    ? task.assignees.map((a) => a.display_name || a.name).filter(Boolean)
    : [];
  if (names.length === 0) return { tooltipLine: 'Not assigned', inline: null };
  const shown = names.slice(0, 2).join(', ');
  const beyond = names.length - 2;
  const tooltipLine = `Assigned: ${shown}${beyond > 0 ? ` +${beyond}` : ''}`;
  const extra = names.length - 1;
  const inline = `${names[0]}${extra > 0 ? ` +${extra}` : ''}`;
  return { tooltipLine, inline };
}

// Small red "not assigned" warning triangle — SINGLE source reused on the
// task bar AND in the left label column so they stay identical (red-500/
// red-400 light/dark, white+dark halo for contrast on any status colour).
// Gag Gantt only (showAssignees-gated at each call site).
function UnassignedMarker({ className = '' }) {
  return (
    <svg width="11" height="11" viewBox="0 0 12 12" className={`text-red-500 dark:text-red-400 ${className}`}
      style={{ filter: 'drop-shadow(0 0 1px rgba(255,255,255,0.95)) drop-shadow(0 0 1px rgba(0,0,0,0.45))' }}
      aria-hidden="true">
      <path d="M6 1 L11.3 11 L0.7 11 Z" fill="currentColor" />
    </svg>
  );
}

const ROW_HEIGHT = 42;
const BAR_HEIGHT = GANTT_BAR_HEIGHT; // 22 since 2026-10-13 (was 30), shared
const GROUP_HEIGHT = 36;
const HEADER_HEIGHT = 48;
const LABEL_WIDTH = 220;

const CATEGORY_HEIGHT = 28;
const MILESTONE_LANE_HEIGHT = 30;

// ── Milestone marker shapes (Task 3.3) ──────────────────────
// Colour is keyed by shape so the component stays generic — callers pick the
// shape per milestone. Every colour has a dark-mode variant; the SVG uses
// currentColor so the same class drives both the marker and its guide line.
export const MILESTONE_SHAPE_CLASSES = {
  diamond: 'text-rose-500 dark:text-rose-400',
  triangle: 'text-amber-500 dark:text-amber-400',
  circle: 'text-emerald-500 dark:text-emerald-400',
  square: 'text-violet-500 dark:text-violet-400',
};

// THE "nobody is on this task" BAR (owner 2026-10-13), shared by the Labour
// view and the Breakdown Timeline: the bar's own colour faded, a red border.
// Inline hex, so index.css's dark-mode colour table cannot repaint it, and an
// INSET RING rather than a border: `html.dark .shadow-sm` forces a pale border
// with !important, which beat an inline red one on the Timeline's bars (browser,
// 2026-10-13). A caller whose bar carries a shadow class drops it for these.
// Colour is never the only signal: each caller also says "Unassigned" in words.
// "Faded red please ... just the outside of the bar, keep the internal faded
// gag colour" (owner, same day): the ring is a softer red, the fill the bar's own.
export const UNASSIGNED_RED = '#EF4444';
// A bar colour is a 6-digit hex wherever one is stored; an alpha suffix on anything
// else is an INVALID colour and the bar would vanish, so that is returned unfaded.
export const withHexAlpha = (color, alpha) => (/^#[0-9a-f]{6}$/i.test(color || '') ? `${color}${alpha}` : color);
export const fadedBarFill = (color) => withHexAlpha(color, '33');
// THE "nobody is on this" ring, on its own: a gag with NO OWNER wears it over
// its full status colour (owner 2026-10-13), a task with no assignee over a faded fill.
export const UNASSIGNED_RING = `inset 0 0 0 2px ${UNASSIGNED_RED}99`;
export const unassignedBarStyle = (color) => ({ backgroundColor: fadedBarFill(color), boxShadow: UNASSIGNED_RING });
// A gag nobody owns (`owner_id` null or absent). Display only.
export const hasNoOwner = (gag) => gag.owner_id == null;
// ...and still wants one: a done or cancelled gag is past needing an owner (owner 2026-10-13).
export const needsOwner = (gag) => hasNoOwner(gag) && gag.status !== 'done' && gag.status !== 'cancelled';
// Asks only when the task CARRIES an assignee list (the gag-detail shape).
export const isUnassignedTask = (task) => Array.isArray(task.assignees) && task.assignees.length === 0;

export function MilestoneShape({ shape }) {
  switch (shape) {
    case 'triangle':
      return <svg className="w-3 h-3 flex-shrink-0" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true"><polygon points="1.5,0.5 11,6 1.5,11.5" /></svg>;
    case 'circle':
      return <svg className="w-3 h-3 flex-shrink-0" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true"><circle cx="6" cy="6" r="5" /></svg>;
    case 'square':
      return <svg className="w-3 h-3 flex-shrink-0" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true"><rect x="1.5" y="1.5" width="9" height="9" rx="1" /></svg>;
    case 'diamond':
    default:
      return <svg className="w-3 h-3 flex-shrink-0" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true"><polygon points="6,0.5 11.5,6 6,11.5 0.5,6" /></svg>;
  }
}

/* ═══ THE SHARED "A GANTT DRAG BECOMES DATES" RULES (exported 2026-10-12) ═══
   ONE definition of how a pointer drag turns into task or milestone dates,
   used by this chart (the gag panel's task Gantt, My Tasks) AND by the
   Breakdown Timeline's expanded gag rows (`gags/GagTimelineView.js`, which
   imports these — its shoot diamond included). Import them; never write the
   arithmetic a second time.
     ganttDayIdxFromClientX   pointer x → day index on the axis
     ganttBarSpan             a task's dates → { left, width }
     ganttTaskDragZones       which drag zones a bar offers (single-day rule)
     ganttTaskDragDates       move / resize-left / resize-right → new dates
     useGanttTaskDrag         press → move → release → a pending confirm
     useGanttMilestoneDrag    the same for a one-date marker (day-snap)
   🔑 They decide NOTHING about who may drag: each caller gates the press and
   owns the save. 🪤 They live in THIS file, not in `hooks/`, because
   `CrossTeamGanttView.dragNullBound.test.js` pins the null-bound guard as
   source text here; behaviour is pinned by `CrossTeamGanttView.sharedDrag.test.js`. */
export function ganttDayIdxFromClientX(timelineEl, clientX, dayWidth) {
  const rect = timelineEl?.getBoundingClientRect();
  const scrollL = timelineEl?.scrollLeft || 0;
  if (!rect) return 0;
  return Math.floor((clientX - rect.left + scrollL) / dayWidth);
}

export function ganttBarSpan(startStr, dueStr, timelineStart, dayWidth) {
  const s = parseDate(startStr) || parseDate(dueStr);
  const e = parseDate(dueStr) || parseDate(startStr);
  if (!s) return null;
  const left = daysDiff(timelineStart, s) * dayWidth;
  const duration = Math.max(1, daysDiff(s, e) + 1);
  return { left, width: duration * dayWidth };
}

// A one-day task (or one with a single date) has no room for two edge handles:
// its left half moves it and its right half extends it. A multi-day bar gets
// both edge handles only from 24px wide.
export function ganttTaskDragZones(task, barWidth) {
  const isSingleDay = task.start_date === task.due_date || !task.start_date || !task.due_date;
  return { isSingleDay, showBothHandles: !isSingleDay && barWidth >= 24 };
}

// The new { start, due } for a live task drag, or `undefined` to ABORT the move.
// `dragState` is what `useGanttTaskDrag` recorded at the press.
export function ganttTaskDragDates(dragState, { clientX, scrollLeft, dayWidth, timelineStart }) {
  // Compute which day column the mouse is over (absolute position in timeline)
  const mouseXInTimeline = clientX - dragState.timelineLeft + scrollLeft;
  const hoveredDayIdx = Math.floor(mouseXInTimeline / dayWidth);
  const hoveredDate = addDays(timelineStart, hoveredDayIdx);

  const origStart = parseDate(dragState.originalStart);
  const origDue = parseDate(dragState.originalDue);
  // 🚨 A NULL BOUND ABORTS THE DRAG — the outcome the old private `toDateStr`
  // produced BY ACCIDENT, made explicit (2026-09-08). `toDateStr` here is now
  // `utils/dateFormat#toLocalISO`, which returns `''` for a falsy argument
  // where the deleted copy THREW, and the `left` branch below can hand it a
  // null: `hoveredDate > origDue` coerces to `Number(date) > 0`, which is
  // TRUE when `origDue` is null, so the clamp selects the null bound. (The
  // `right` branch's `hoveredDate < origStart` is `Number(date) < 0` — false —
  // so it selects `hoveredDate` and was never exposed.) Without this, an
  // empty start date reaches the confirm dialog and then `onDragComplete`,
  // i.e. a task written with no start date, where the TypeError used to make
  // the bar snap back.
  // 🪤 REACHABLE, and not only through a task with no dates: the left handle
  // renders only when both dates exist and differ, but `ganttDates#parseDate`
  // also returns null for a TRUTHY value it cannot parse — an ISO DATETIME,
  // the exact hazard root `CLAUDE.md` records for this function. So a
  // `due_date` arriving as `2026-01-05T00:00:00Z` is the live path.
  if (!origStart || !origDue) return;

  if (dragState.handle === 'right') {
    // Due date = the day the mouse is over (but not before start)
    const newDue = hoveredDate < origStart ? origStart : hoveredDate;
    return { start: dragState.originalStart, due: toDateStr(newDue) };
  }
  if (dragState.handle === 'left') {
    // Start date = the day the mouse is over (but not after due)
    const newStart = hoveredDate > origDue ? origDue : hoveredDate;
    return { start: toDateStr(newStart), due: dragState.originalDue };
  }
  // Body drag — shift both by delta from start position
  const deltaX = clientX - dragState.startX;
  const deltaDays = Math.round(deltaX / dayWidth);
  return { start: toDateStr(addDays(origStart, deltaDays)), due: toDateStr(addDays(origDue, deltaDays)) };
}

// The four window listeners a live drag needs, removed when it ends.
function useWindowDragListeners(active, onMove, onEnd) {
  useEffect(() => {
    if (!active) return undefined;
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onEnd);
    window.addEventListener('touchmove', onMove);
    window.addEventListener('touchend', onEnd);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onEnd);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
    };
  }, [active, onMove, onEnd]);
}

/**
 * Task-bar drag: `startTaskDrag(e, task, handle)` with handle 'body' | 'left' |
 * 'right'. While live, `dragState.currentStart/currentDue` are the dates under
 * the pointer; on release `confirmDialog` holds `{ task, newStart, newDue,
 * oldStart, oldDue }` for the caller's confirm step (clear it with
 * `setConfirmDialog(null)`). `dragEndTime` (a ref the caller owns) is stamped
 * only when a date MOVED, so the caller's click handlers can ignore the click
 * a release fires. `onStart` runs at the press (hide hover cards).
 */
export function useGanttTaskDrag({ timelineRef, dayWidth, timelineStart, dragEndTime, onStart }) {
  const [dragState, setDragState] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);

  const startTaskDrag = (e, task, handle) => {
    e.preventDefault();
    e.stopPropagation();
    if (onStart) onStart();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const rect = timelineRef.current?.getBoundingClientRect();
    const scrollL = timelineRef.current?.scrollLeft || 0;
    setDragState({
      task, handle,
      startX: clientX,
      timelineLeft: rect?.left || 0,
      scrollLeftAtStart: scrollL,
      originalStart: task.start_date || task.due_date,
      originalDue: task.due_date || task.start_date,
      currentStart: task.start_date || task.due_date,
      currentDue: task.due_date || task.start_date,
    });
  };

  const handleDragMove = useCallback((e) => {
    if (!dragState) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const scrollLeft = timelineRef.current?.scrollLeft || 0;
    const next = ganttTaskDragDates(dragState, { clientX, scrollLeft, dayWidth, timelineStart });
    if (!next) return;
    if (next.start === dragState.currentStart && next.due === dragState.currentDue) return;
    setDragState((prev) => (prev ? { ...prev, currentStart: next.start, currentDue: next.due } : null));
  }, [dragState, dayWidth, timelineStart, timelineRef]);

  const handleDragEnd = useCallback(() => {
    if (!dragState) return;
    const { task, currentStart, currentDue, originalStart, originalDue } = dragState;
    setDragState(null);
    // Only stamp the click-suppression window when the dates actually MOVED.
    // A plain click (mousedown+up with no travel — or a few px of jitter,
    // which day-snapping resolves to the SAME dates) must fall through to
    // the bar's onClick and open the task details; draggable users were
    // otherwise locked out of the detail popup entirely.
    if (currentStart === originalStart && currentDue === originalDue) return;
    dragEndTime.current = Date.now();
    setConfirmDialog({ task, newStart: currentStart, newDue: currentDue, oldStart: originalStart, oldDue: originalDue });
  }, [dragState, dragEndTime]);

  useWindowDragListeners(!!dragState, handleDragMove, handleDragEnd);

  // Live mirror (`gantt/ganttLiveDrag`): this chart's drag — and its pending
  // confirm — is published; `remoteTaskDates(task)` is ANOTHER chart's live
  // dates for a task this one is not itself moving.
  const ownId = dragState ? dragState.task.id : confirmDialog ? confirmDialog.task.id : null;
  const ownDates = dragState
    ? { start: dragState.currentStart, due: dragState.currentDue }
    : confirmDialog ? { start: confirmDialog.newStart, due: confirmDialog.newDue } : null;
  usePublishGanttLiveDrag(ownId != null ? ganttTaskLiveKey(ownId) : null, ownDates);
  const liveDrags = useGanttLiveDrags();
  const remoteTaskDates = (task) => (task.id === ownId ? null : liveDrags[ganttTaskLiveKey(task.id)] || null);

  return { dragState, confirmDialog, setConfirmDialog, startTaskDrag, remoteTaskDates };
}

const milestoneFieldKey = (m) => m.field;

/**
 * Milestone (one-date marker) drag — the same absolute-mouse day-snap as task
 * bars, clamped to the axis. `startMilestoneDrag(e, milestone)` takes any object
 * with a `date`; on release `msConfirm` holds `{ milestone, oldDate, newDate }`.
 * `milestoneDisplayDate(m)` is the live position: dragging > pending-confirm
 * ghost > saved date. `keyOf` says which milestone is which — the field by
 * default (one gag per chart); a chart showing SEVERAL gags passes its own.
 */
export function useGanttMilestoneDrag({
  timelineRef, dayWidth, timelineStart, totalDays, dragEndTime, onStart, keyOf = milestoneFieldKey,
}) {
  const [msDrag, setMsDrag] = useState(null); // { milestone, currentDate }
  const [msConfirm, setMsConfirm] = useState(null); // { milestone, oldDate, newDate }

  const startMilestoneDrag = (e, milestone) => {
    e.preventDefault();
    e.stopPropagation();
    if (onStart) onStart();
    setMsDrag({ milestone, currentDate: milestone.date });
  };

  const handleMsDragMove = useCallback((e) => {
    if (!msDrag) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const dayIdx = Math.max(0, Math.min(totalDays, ganttDayIdxFromClientX(timelineRef.current, clientX, dayWidth)));
    const newDate = toDateStr(addDays(timelineStart, dayIdx));
    if (newDate === msDrag.currentDate) return;
    setMsDrag((prev) => (prev ? { ...prev, currentDate: newDate } : null));
  }, [msDrag, timelineRef, dayWidth, timelineStart, totalDays]);

  const handleMsDragEnd = useCallback(() => {
    if (!msDrag) return;
    const { milestone, currentDate } = msDrag;
    setMsDrag(null);
    // Same no-move rule as the task drag: a plain click on a marker must not
    // open the 500ms click-suppression window (it would swallow an
    // immediately-following task-bar click too).
    if (currentDate === milestone.date) return;
    dragEndTime.current = Date.now();
    setMsConfirm({ milestone, oldDate: milestone.date, newDate: currentDate });
  }, [msDrag, dragEndTime]);

  useWindowDragListeners(!!msDrag, handleMsDragMove, handleMsDragEnd);

  const isMilestoneDragging = (m) => !!msDrag && keyOf(msDrag.milestone) === keyOf(m);
  const isMilestonePending = (m) => !!msConfirm && keyOf(msConfirm.milestone) === keyOf(m);
  // Live mirror: a milestone carrying a `liveKey` publishes its drag (and
  // pending confirm) and shows another chart's (`gantt/ganttLiveDrag`).
  const ownMs = msDrag ? msDrag.milestone : msConfirm ? msConfirm.milestone : null;
  usePublishGanttLiveDrag(
    ownMs ? ownMs.liveKey : null,
    msDrag ? { date: msDrag.currentDate } : msConfirm ? { date: msConfirm.newDate } : null,
  );
  const liveDrags = useGanttLiveDrags();
  // Live position: dragging > pending-confirm ghost > another chart's live
  // drag > saved date.
  const milestoneDisplayDate = (m) => {
    if (isMilestoneDragging(m)) return msDrag.currentDate;
    if (isMilestonePending(m)) return msConfirm.newDate;
    const remote = m.liveKey ? liveDrags[m.liveKey] : null;
    if (remote && remote.date) return remote.date;
    return m.date;
  };

  return {
    msDrag, msConfirm, setMsConfirm, startMilestoneDrag,
    milestoneDisplayDate, isMilestoneDragging, isMilestonePending,
  };
}

/* 📜 `pageScroll` (2026-10-05, owner: *"check all gannt charts for all the
   updates we are making"*) — the Crew Gantt's page-scroll layout, for a caller
   that renders this chart at PAGE level (My Tasks). Off (default) = the capped
   `maxHeight 600` both-axes scroller with its 1:1 `teams-gantt-labels` mirror,
   unchanged — the NESTED callers (a gag panel inside the Breakdown table's x
   pane, a Manage Teams row) keep it, because a sticky band inside another
   scroller pins to THAT scroller, not the page. On: no inner vertical scroller;
   the label header + date band pin at `stickyTop` (the caller's measured
   `useStickyToolbar().headerTop`), 32px tall; edge fade + a sticky bottom bar —
   all through `hooks/useGanttScrollFrame`. The root becomes `isolate` +
   `overflow-clip` (never `overflow-hidden`, which would capture both
   stickies), so its in-tree overlays portal to `document.body`. */
export const CROSS_TEAM_PAGE_SCROLL_HEADER_HEIGHT = 32;

export default function CrossTeamGanttView({ tasks, onTaskClick, canDrag, onDragComplete, onEditTask, onViewDetails, onCreateTask, onDeleteTask, categoryField, categoryOrderField, milestones, onMilestoneDrag, showAssignees = false, showSummary = false, statusColorFallback = false, pageScroll = false, stickyTop = 0, hideSort = false }) {
  const [sortBy, setSortBy] = useState('date');
  const [sortDir, setSortDir] = useState('start'); // 'start' or 'end' when sortBy=date; 'asc'/'desc' when sortBy=name
  const [collapsed, setCollapsed] = useState({});
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [createDrag, setCreateDrag] = useState(null); // { teamId, startDayIdx, currentDayIdx }
  const [msConfirmLoading, setMsConfirmLoading] = useState(false);
  const [msTooltip, setMsTooltip] = useState(null); // { label, date, x, y }
  const timelineRef = useRef(null);
  // Zoom state, pixels per day and the keep-the-centre rule (shared).
  const { zoom, dayWidth, zoomTo, applyCentre } = useGanttZoom(timelineRef);
  // `pageScroll`: the timeline pans x only and the body's vertical pan moves the
  // WINDOW (the body ref is attached only in that mode).
  const bodyRef = useRef(null);
  useDragScroll(timelineRef, pageScroll ? { axis: 'x' } : { axis: 'both', syncLabelsId: 'teams-gantt-labels' });
  useDragScroll(bodyRef, { axis: 'y', verticalPansWindow: true });
  const {
    stickyBarRef, overflows: timelineOverflows, scrollWidth: timelineScrollWidth,
    fadeStyle: timelineFadeStyle, readFade: readTimelineFade,
    dateBandRef, dateAxisClipRef, handleBandWheel,
  } = useGanttScrollFrame(timelineRef, { fade: pageScroll });
  // The pinned date band's live bottom (`pageScroll`): a card that would sit
  // above it flips BELOW its bar (`gantt/GanttBarTooltip`).
  const getPinnedBottom = useCallback(() => (pageScroll && dateBandRef.current
    ? dateBandRef.current.getBoundingClientRect().bottom : 0), [pageScroll, dateBandRef]);
  const headerHeight = pageScroll ? CROSS_TEAM_PAGE_SCROLL_HEADER_HEIGHT : HEADER_HEIGHT;
  const outOfChart = (node) => (pageScroll ? createPortal(node, document.body) : node);
  const todayLineRef = useRef(null);
  const dragEndTime = useRef(0);
  const [spanTooltip, setSpanTooltip] = useState(null); // collapsed-group rollup bar hover

  const pxToDays = useCallback((px) => Math.round(px / dayWidth), [dayWidth]);

  // Group tasks by team, then sort within each group
  const { groups, categories } = useMemo(() => {
    const teamMap = {};
    for (const task of tasks) {
      if (!task.start_date && !task.due_date) continue;
      const teamName = task.team_name || 'Unassigned';
      const teamId = task.team_id || 0;
      if (!teamMap[teamId]) teamMap[teamId] = { teamId, teamName, tasks: [], category: categoryField ? task[categoryField] : null, categoryOrder: categoryOrderField ? task[categoryOrderField] : 0 };
      teamMap[teamId].tasks.push(task);
    }
    let result = Object.values(teamMap);
    // Sort tasks within each group
    for (const group of result) {
      group.tasks.sort((a, b) => {
        if (sortBy === 'date') {
          const field = sortDir === 'end' ? 'due_date' : 'start_date';
          return (a[field] || '9999').localeCompare(b[field] || '9999');
        }
        const cmp = (a.title || '').localeCompare(b.title || '');
        return sortDir === 'desc' ? -cmp : cmp;
      });
    }
    // Sort groups: by category order first, then by name within category
    if (categoryField) {
      result.sort((a, b) => (a.categoryOrder - b.categoryOrder) || a.teamName.localeCompare(b.teamName));
    } else {
      result.sort((a, b) => a.teamName.localeCompare(b.teamName));
    }
    // Build category list (ordered, unique)
    const cats = categoryField
      ? [...new Map(result.map(g => [g.category, g.categoryOrder])).entries()]
          .sort((a, b) => a[1] - b[1])
          .map(([name]) => name)
      : [];
    return { groups: result, categories: cats };
  }, [tasks, sortBy, sortDir, categoryField, categoryOrderField]);

  const teamColorMap = useMemo(() => {
    const map = {};
    groups.forEach((g, i) => {
      // Prefer team_color from API, fall back to auto-assigned palette
      const apiColor = g.tasks[0]?.team_color;
      map[g.teamId] = { hex: apiColor || TEAM_COLORS[i % TEAM_COLORS.length].hex };
    });
    return map;
  }, [groups]);

  // Rolled-up span per group (collapsed-group summary bar): earliest start →
  // latest due over the group's DATED tasks. total = all tasks in the group
  // (matches the header count); null when nothing in the group is dated (no
  // summary bar then). Dates are stored as strings so the render reuses the
  // exact same date→x math (daysDiff/dayWidth) as the task bars.
  const groupSpans = useMemo(() => {
    const m = {};
    for (const g of groups) {
      let min = null, max = null;
      for (const t of g.tasks) {
        const s = parseDate(t.start_date) || parseDate(t.due_date);
        const e = parseDate(t.due_date) || parseDate(t.start_date);
        if (!s) continue;
        if (!min || s < min) min = s;
        if (!max || e > max) max = e;
      }
      m[g.teamId] = min ? { start: toDateStr(min), end: toDateStr(max), total: g.tasks.length } : null;
    }
    return m;
  }, [groups]);

  // Milestone lane (Task 3.3) — OPTIONAL prop; existing callers pass nothing
  // and every milestone behaviour below is guarded on this list being non-empty.
  const laneMilestones = useMemo(
    () => (milestones || []).filter((m) => parseDate(m.date)),
    [milestones]
  );
  const showMilestoneLane = laneMilestones.length > 0;

  // Timeline range — snap to Monday. Milestone dates extend the range so a
  // shoot/strike date beyond every task due date is still on the axis.
  const { timelineStart, totalDays } = useMemo(() => {
    const datedTasks = tasks.filter(t => t.start_date || t.due_date);
    const msDates = laneMilestones.map((m) => parseDate(m.date));
    if (datedTasks.length === 0 && msDates.length === 0) {
      const now = new Date(); now.setHours(0, 0, 0, 0);
      return { timelineStart: now, totalDays: 30 };
    }
    let minDate = null, maxDate = null;
    for (const t of datedTasks) {
      const s = parseDate(t.start_date) || parseDate(t.due_date);
      const e = parseDate(t.due_date) || parseDate(t.start_date);
      if (!minDate || s < minDate) minDate = s;
      if (!maxDate || e > maxDate) maxDate = e;
    }
    for (const d of msDates) {
      if (!minDate || d < minDate) minDate = d;
      if (!maxDate || d > maxDate) maxDate = d;
    }
    const start = addDays(minDate, -7);
    const dow = start.getDay();
    if (dow !== 1) start.setDate(start.getDate() - ((dow + 6) % 7)); // Snap to Monday
    const end = addDays(maxDate, 14);
    return { timelineStart: start, totalDays: Math.max(daysDiff(start, end), 30) };
  }, [tasks, laneMilestones]);

  const todayOffset = useMemo(() => {
    const t = new Date(); t.setHours(0, 0, 0, 0);
    return daysDiff(timelineStart, t);
  }, [timelineStart]);

  // Scroll to today (first paint, new groups); a zoom change keeps the centre.
  useEffect(() => {
    if (applyCentre()) return;
    if (todayLineRef.current && timelineRef.current) {
      const todayPos = todayOffset * dayWidth;
      timelineRef.current.scrollLeft = Math.max(0, todayPos - timelineRef.current.clientWidth / 3);
    }
  }, [todayOffset, dayWidth, groups, applyCentre]);

  // Build date headers (shared axis builder — utils/ganttDates.js)
  const dateHeaders = useMemo(() => buildDateHeaders(zoom, timelineStart, totalDays), [timelineStart, totalDays, zoom]);

  const totalWidth = totalDays * dayWidth;

  // ── Drag state — the shared hooks above. A press hides the hover cards
  // (through a ref: the bar tooltip's own hook needs the drag state first).
  const hideCardsRef = useRef({});
  const { dragState, confirmDialog, setConfirmDialog, startTaskDrag, remoteTaskDates } = useGanttTaskDrag({
    timelineRef, dayWidth, timelineStart, dragEndTime, onStart: () => hideCardsRef.current.task?.(),
  });
  const {
    msDrag, msConfirm, setMsConfirm, startMilestoneDrag,
    milestoneDisplayDate: getMilestoneDisplayDate, isMilestoneDragging, isMilestonePending,
  } = useGanttMilestoneDrag({
    timelineRef, dayWidth, timelineStart, totalDays, dragEndTime, onStart: () => hideCardsRef.current.milestone?.(),
  });
  // The task bar's hover card — `gantt/GanttBarTooltip`, shared with the crew
  // and hires Gantts: anchored ABOVE the bar with an 8px `h-2` bridge (it used
  // to sit at the cursor, +12/−10, covering the bar to the cursor's right and
  // swallowing the mousedown for a drag — the 2026-10-05 hires defect).
  const {
    tooltip, setTooltip, show: showBarTooltip, hide: hideTooltip, cancelHide: cancelHideTooltip,
  } = useGanttBarTooltip(!!(dragState || msDrag));
  hideCardsRef.current = {
    task: () => setTooltip(null),
    milestone: () => { setTooltip(null); setMsTooltip(null); },
  };

  // Rows
  const rows = useMemo(() => {
    const result = [];
    let lastCategory = null;
    for (const group of groups) {
      // Insert category header if category changed
      if (categoryField && group.category !== lastCategory) {
        result.push({ type: 'category', category: group.category });
        lastCategory = group.category;
      }
      result.push({ type: 'group', teamId: group.teamId, teamName: group.teamName, taskCount: group.tasks.length });
      if (!collapsed[group.teamId]) {
        for (const task of group.tasks) result.push({ type: 'task', task, teamId: group.teamId });
      }
    }
    return result;
  }, [groups, collapsed, categoryField]);

  const totalRowHeight = rows.reduce((sum, r) => sum + (r.type === 'category' ? CATEGORY_HEIGHT : r.type === 'group' ? GROUP_HEIGHT : ROW_HEIGHT), 0);
  const toggleGroup = (teamId) => setCollapsed(prev => ({ ...prev, [teamId]: !prev[teamId] }));

  // Bar style helper
  const getBarStyle = (task) => ganttBarSpan(task.start_date, task.due_date, timelineStart, dayWidth);

  const getDragBar = (task) => {
    if (dragState && dragState.task.id === task.id) return ganttBarSpan(dragState.currentStart, dragState.currentDue, timelineStart, dayWidth);
    // Another chart is dragging this task: follow it live.
    const remote = remoteTaskDates(task);
    return remote ? ganttBarSpan(remote.start, remote.due, timelineStart, dayWidth) : null;
  };

  // ── Tooltip ── (state + delayed hide: `useGanttBarTooltip` above)
  const showTooltip = (e, task) => showBarTooltip(e, { task });

  // ── Click-to-create on group header rows ──
  const getDayIdxFromMouseX = useCallback(
    (clientX) => ganttDayIdxFromClientX(timelineRef.current, clientX, dayWidth),
    [dayWidth],
  );

  const handleCreateMouseDown = useCallback((e, teamId) => {
    if (!onCreateTask) return;
    e.preventDefault();
    const dayIdx = getDayIdxFromMouseX(e.clientX);
    setCreateDrag({ teamId, startDayIdx: dayIdx, currentDayIdx: dayIdx });
  }, [onCreateTask, getDayIdxFromMouseX]);

  useEffect(() => {
    if (!createDrag) return;
    const handleMove = (e) => {
      const dayIdx = getDayIdxFromMouseX(e.clientX);
      setCreateDrag(prev => prev ? { ...prev, currentDayIdx: dayIdx } : null);
    };
    const handleUp = () => {
      if (createDrag) {
        const startIdx = Math.min(createDrag.startDayIdx, createDrag.currentDayIdx);
        const endIdx = Math.max(createDrag.startDayIdx, createDrag.currentDayIdx);
        const startDate = toDateStr(addDays(timelineStart, startIdx));
        const dueDate = startIdx === endIdx ? null : toDateStr(addDays(timelineStart, endIdx));
        onCreateTask(createDrag.teamId, startDate, dueDate);
      }
      setCreateDrag(null);
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => { window.removeEventListener('mousemove', handleMove); window.removeEventListener('mouseup', handleUp); };
  }, [createDrag, getDayIdxFromMouseX, timelineStart, onCreateTask]);

  // ── Drag logic — `useGanttTaskDrag` / `useGanttMilestoneDrag` above; this
  // chart only GATES the press and owns the confirm + save. ──
  const handleDragStart = (e, task, handle) => {
    if (!canDrag) return;
    startTaskDrag(e, task, handle);
  };

  const handleConfirm = async () => {
    if (!confirmDialog || !onDragComplete) return;
    setConfirmLoading(true);
    try { await onDragComplete(confirmDialog.task, confirmDialog.newStart, confirmDialog.newDue); } catch (err) { console.error(err); }
    setConfirmLoading(false);
    setConfirmDialog(null);
  };

  // ── Milestone drag (Task 3.3) — same absolute-mouse day-snap as task bars ──
  const handleMsDragStart = (e, milestone) => {
    if (!milestone.draggable || !onMilestoneDrag) return;
    startMilestoneDrag(e, milestone);
  };

  const handleMsConfirm = async () => {
    if (!msConfirm || !onMilestoneDrag) return;
    setMsConfirmLoading(true);
    try { await onMilestoneDrag(msConfirm.milestone.field, msConfirm.newDate); } catch (err) { console.error(err); }
    setMsConfirmLoading(false);
    setMsConfirm(null);
  };

  // A milestone-only chart (no dated tasks) still renders: rows is empty and
  // totalRowHeight is 0, so the grid/today-marker/rows sections degrade to
  // zero-height while the axis (driven by milestone dates) and lane remain.
  if (groups.length === 0 && !showMilestoneLane) {
    return <div className="text-center py-8 text-sm text-gray-500 dark:text-gray-400 italic">No tasks with dates across your teams.</div>;
  }

  // The label column's rows (milestone lane + every row), rendered by both
  // column layouts below.
  const labelRows = (
    <>
    {showMilestoneLane && (
      <div
        className="flex items-center px-3 border-b border-gray-200 dark:border-gray-600 bg-gray-100/70 dark:bg-gray-800/60"
        style={{ height: MILESTONE_LANE_HEIGHT }}>
        <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400">Milestones</span>
      </div>
    )}
    {rows.map((row, rowIdx) => {
      if (row.type === 'category') {
        return (
          <div key={`cat-${row.category}`}
            className="flex items-center px-3 border-b border-gray-300 dark:border-gray-500 bg-gray-200/50 dark:bg-gray-700/50"
            style={{ height: CATEGORY_HEIGHT }}>
            <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400">{row.category}</span>
          </div>
        );
      }
      if (row.type === 'group') {
        return (
          <div key={`g-${row.teamId}`}
            className="flex items-center px-2 gap-2 border-b border-gray-200 dark:border-gray-600 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700/30 font-semibold"
            style={{ height: GROUP_HEIGHT, backgroundColor: (teamColorMap[row.teamId]?.hex || '#666') + '10' }}
            onClick={() => toggleGroup(row.teamId)}>
            <span className={`text-gray-400 text-xs transition-transform ${collapsed[row.teamId] ? '' : 'rotate-90'}`}>&#9654;</span>
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: teamColorMap[row.teamId]?.hex }} />
            <span className="text-xs text-gray-800 dark:text-gray-200 truncate">{row.teamName}</span>
            <span className="text-xs text-gray-400 dark:text-gray-500 ml-auto">{row.taskCount}</span>
          </div>
        );
      }
      // Leading red triangle on UNASSIGNED rows (gag Gantt only) —
      // identical to the bar marker, so unassigned tasks are scannable
      // in the label list too. flex-shrink-0 so the title truncates
      // after it, never the marker.
      const rowUnassigned = showAssignees && assigneeInfo(row.task).inline == null;
      return (
        <div key={`t-${row.task.id}`}
          className="flex items-center px-3 pl-7 border-b border-gray-100 dark:border-gray-700 text-sm text-gray-600 dark:text-gray-300 min-w-0 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/30"
          style={{ height: ROW_HEIGHT }}
          onClick={() => onTaskClick && onTaskClick(row.task)}
          // Combine the existing full-title-on-hover with the assignee
          // line (gag Gantt only) — newline-separated native tooltip.
          title={showAssignees ? `${row.task.title}\n${assigneeInfo(row.task).tooltipLine}` : row.task.title}>
          {rowUnassigned && <UnassignedMarker className="mr-1.5 flex-shrink-0" />}
          <span className="truncate">{row.task.title}</span>
        </div>
      );
    })}
    </>
  );

  return (
    <div className={pageScroll
      ? 'isolate bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-clip'
      : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden'}
    >
      {/* Zoom + Legend */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-4">
          {/* Sort controls — `hideSort` (My Tasks, owner 2026-10-12): View by
              already arranges that page, so its bars stay in start-date order. */}
          {!hideSort && (
          <div className="flex items-center gap-2" data-testid="gantt-sort-controls">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Sort</span>
            <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
              {['name', 'date'].map(s => (
                <button key={s} onClick={() => { setSortBy(s); if (s === 'date') setSortDir('start'); else setSortDir('asc'); }}
                  className={`px-3 py-1.5 text-xs font-medium capitalize transition-colors ${sortBy === s ? 'bg-blue-600 dark:bg-blue-500/70 text-white' : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'}`}>
                  {s}
                </button>
              ))}
            </div>
            <button
              onClick={() => {
                if (sortBy === 'date') setSortDir(prev => prev === 'start' ? 'end' : 'start');
                else setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
              }}
              className="p-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              title={sortBy === 'date' ? (sortDir === 'end' ? 'Sort by end date' : 'Sort by start date') : (sortDir === 'asc' ? 'A → Z' : 'Z → A')}
            >
              <svg className={`w-4 h-4 transition-transform ${sortDir === 'desc' || sortDir === 'end' ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
              </svg>
            </button>
            <span className="text-[10px] text-gray-400 dark:text-gray-500">
              {sortBy === 'date' ? (sortDir === 'end' ? 'End date' : 'Start date') : (sortDir === 'asc' ? 'A → Z' : 'Z → A')}
            </span>
          </div>
          )}

          {/* Zoom controls */}
          <div className="flex items-center gap-2">
            <GanttZoomControl zoom={zoom} onZoom={zoomTo} />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3 text-xs text-gray-700 dark:text-gray-200">
          {groups.map(g => (
            <div key={g.teamId} className="flex items-center gap-1">
              <span className="w-3 h-3 rounded" style={{ backgroundColor: teamColorMap[g.teamId]?.hex }} />
              <span className="text-xs text-gray-500 dark:text-gray-400">{g.teamName}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Gantt body — `pageScroll`: a `relative` box holding the page-pinned
          date band, then the label column + x-only timeline row. Otherwise the
          capped both-axes scroller it always was. */}
      <div
        ref={pageScroll ? bodyRef : undefined}
        data-testid="cross-team-gantt-body"
        className={pageScroll ? 'relative' : 'flex overflow-hidden'}
        style={pageScroll ? undefined : { maxHeight: '600px' }}
      >
        {pageScroll && (
          /* 📌 The date band — OUTSIDE the timeline (a sticky child of an
             x-only scroller is inert), opaque, `GANTT_AXIS_Z`, pinned at the
             measured `stickyTop`. Its clip follows the timeline; a drag or
             `deltaX` wheel on it pans the timeline. */
          <div
            ref={dateBandRef}
            data-testid="cross-team-gantt-date-band"
            className={`sticky top-0 ${GANTT_AXIS_Z} flex cursor-grab bg-white dark:bg-gray-800`}
            style={{ height: headerHeight, top: stickyTop }}
            onWheel={handleBandWheel}
          >
            <div className="flex-shrink-0 border-r border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 px-3 flex items-center" style={{ width: LABEL_WIDTH }}>
              <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Task</span>
            </div>
            <div ref={dateAxisClipRef} data-testid="cross-team-gantt-date-band-clip" className="flex-1 min-w-0 overflow-hidden" style={timelineFadeStyle}>
              <GanttDateAxis
                testId="cross-team-gantt-date-axis"
                className="relative"
                style={{ width: totalWidth, minWidth: '100%' }}
                dateHeaders={dateHeaders}
                dayWidth={dayWidth}
                height={headerHeight}
              />
            </div>
          </div>
        )}
        <div className={pageScroll ? 'flex' : 'contents'}>
        {/* Fixed left label column. `pageScroll`: a plain column (the band's
            corner carries the heading). Otherwise the capped column whose labels
            div is MIRRORED 1:1 (`gantt-label-scroll-sync.test.js` reads this). */}
        {pageScroll ? (
          <div className="flex-shrink-0 border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900" style={{ width: LABEL_WIDTH }}>
            {labelRows}
          </div>
        ) : (
        <div className="flex-shrink-0 border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 overflow-hidden flex flex-col" style={{ width: LABEL_WIDTH }}>
          <div className="flex-shrink-0 border-b border-gray-200 dark:border-gray-700 px-3 flex items-center" style={{ height: headerHeight }}>
            <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Task</span>
          </div>
          <div id="teams-gantt-labels" className="flex-1 overflow-hidden">
            {labelRows}
          </div>
        </div>
        )}

        {/* Scrollable timeline */}
        <div className={pageScroll ? `flex-1 ${PAGE_SCROLL_PANE_X} cursor-grab` : 'flex-1 overflow-auto cursor-grab'} ref={timelineRef}
          data-testid="cross-team-gantt-timeline-scrollport"
          style={timelineFadeStyle}
          onScroll={(e) => { if (pageScroll) { readTimelineFade(); return; } const labels = document.getElementById('teams-gantt-labels'); if (labels) labels.scrollTop = e.target.scrollTop; }}>
          <div style={{ width: totalWidth, minWidth: '100%' }}>
            {/* Date headers (`pageScroll`: in the band above instead) */}
            {!pageScroll && (
            <div className="relative border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800" style={{ height: HEADER_HEIGHT }}>
              {dateHeaders.map((h, i) => (
                <div key={i}
                  className={`absolute top-0 flex flex-col items-start justify-center px-1 border-r border-gray-100 dark:border-gray-700 text-xs ${h.isWeekend ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                  style={{ left: h.offset * dayWidth, width: h.width * dayWidth, height: HEADER_HEIGHT }}>
                  <span className="text-gray-600 dark:text-gray-300 font-medium truncate w-full">{h.label}</span>
                  {h.sublabel && <span className="text-gray-400 dark:text-gray-500 text-[10px]">{h.sublabel}</span>}
                </div>
              ))}
            </div>
            )}

            {/* Milestone lane (Task 3.3) — only rendered when the milestones prop has dated entries */}
            {showMilestoneLane && (
              <div className="relative border-b border-gray-200 dark:border-gray-600 bg-gray-100/70 dark:bg-gray-800/60"
                style={{ height: MILESTONE_LANE_HEIGHT, width: totalWidth }}>
                {laneMilestones.map((m) => {
                  const d = parseDate(getMilestoneDisplayDate(m));
                  if (!d) return null;
                  const centerX = daysDiff(timelineStart, d) * dayWidth + dayWidth / 2;
                  const isMsDragging = isMilestoneDragging(m);
                  const isPending = isMilestonePending(m);
                  const draggableMs = !!m.draggable && !!onMilestoneDrag;
                  return (
                    <div
                      key={m.field}
                      className={`absolute top-0 h-full flex items-center gap-1 ${MILESTONE_SHAPE_CLASSES[m.shape] || MILESTONE_SHAPE_CLASSES.diamond}`}
                      style={{
                        left: centerX - 6,
                        zIndex: isMsDragging ? GANTT_DRAG_Z : 10,
                        opacity: isMsDragging || isPending ? 0.7 : 1,
                        cursor: draggableMs ? GANTT_CURSOR.move : 'default',
                      }}
                      data-pan-skip={draggableMs ? '' : undefined}
                      {...(isMsDragging ? { [GANTT_DRAGGING_ATTR]: '' } : null)}
                      onMouseDown={draggableMs ? (e) => handleMsDragStart(e, m) : undefined}
                      onTouchStart={draggableMs ? (e) => handleMsDragStart(e, m) : undefined}
                      onMouseEnter={(e) => { if (!dragState && !msDrag) setMsTooltip({ label: m.label, date: getMilestoneDisplayDate(m), x: e.clientX + 12, y: e.clientY - 10 }); }}
                      onMouseLeave={() => setMsTooltip(null)}>
                      <MilestoneShape shape={m.shape} />
                      {zoom !== 'quarter' && (
                        <span className="text-[9px] font-semibold uppercase tracking-wide select-none pointer-events-none whitespace-nowrap">
                          {m.label}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Chart rows */}
            <div className="relative">
              {/* Background grid */}
              <GanttGrid dateHeaders={dateHeaders} dayWidth={dayWidth} height={totalRowHeight} />
              <GanttTodayLine todayOffset={todayOffset} totalDays={totalDays} dayWidth={dayWidth} height={totalRowHeight} lineRef={todayLineRef} />

              {/* Milestone guide lines — dashed drop-lines from the lane through the rows */}
              {showMilestoneLane && laneMilestones.map((m) => {
                const d = parseDate(getMilestoneDisplayDate(m));
                if (!d) return null;
                const x = daysDiff(timelineStart, d) * dayWidth + dayWidth / 2;
                return (
                  <div
                    key={`ms-line-${m.field}`}
                    className={`absolute top-0 pointer-events-none ${MILESTONE_SHAPE_CLASSES[m.shape] || MILESTONE_SHAPE_CLASSES.diamond}`}
                    style={{ left: x - 1, height: totalRowHeight, width: 0, borderLeft: '2px dashed currentColor', opacity: 0.3, zIndex: 5 }}
                  />
                );
              })}

              {/* Task rows */}
              {rows.map((row) => {
                if (row.type === 'category') {
                  return (
                    <div key={`cat-${row.category}`}
                      className="relative border-b border-gray-300 dark:border-gray-500 bg-gray-200/30 dark:bg-gray-700/30"
                      style={{ height: CATEGORY_HEIGHT }} />
                  );
                }
                if (row.type === 'group') {
                  const isCreating = createDrag && createDrag.teamId === row.teamId;
                  const createLeft = isCreating ? Math.min(createDrag.startDayIdx, createDrag.currentDayIdx) * dayWidth : 0;
                  const createWidth = isCreating ? (Math.abs(createDrag.currentDayIdx - createDrag.startDayIdx) + 1) * dayWidth : 0;
                  // When collapsed with a rollup (gag Gantt), the whole row is
                  // CLICK-TO-EXPAND, not draggable: pointer cursor (not the
                  // timeline's inherited grab), data-pan-skip so mousedown
                  // doesn't start a pan, and onClick toggles open. The rollup
                  // bar carries NO drag handlers/handles — only its hover
                  // tooltip. Milestones live in their own lane (separate row)
                  // and keep their drag behaviour untouched.
                  const collapsedSummary = showSummary && collapsed[row.teamId];
                  return (
                    <div key={`g-${row.teamId}`}
                      className={`relative border-b border-gray-200 dark:border-gray-600 ${onCreateTask ? 'cursor-cell' : collapsedSummary ? 'cursor-pointer' : ''}`}
                      style={{ height: GROUP_HEIGHT, backgroundColor: (teamColorMap[row.teamId]?.hex || '#666') + '08' }}
                      onMouseDown={(e) => onCreateTask && handleCreateMouseDown(e, row.teamId)}
                      onClick={collapsedSummary ? () => toggleGroup(row.teamId) : undefined}
                      title={collapsedSummary ? 'Click to expand' : undefined}
                      data-pan-skip={(onCreateTask || collapsedSummary) ? '' : undefined}>
                      {isCreating && (
                        <div className="absolute top-1 rounded-md bg-blue-400/30 border border-blue-400/50"
                          style={{ left: createLeft, width: createWidth, height: GROUP_HEIGHT - 8 }} />
                      )}
                      {/* Collapsed-group rolled-up SPAN bar (showSummary-gated —
                          Teams callers keep their empty collapsed header). A
                          slim neutral bar with darker end-caps spanning the
                          group's earliest start → latest due, so it reads as an
                          aggregate, not a task. No assignee/warning markers.
                          Same date→x math as the task bars, so it aligns to the
                          axis exactly. */}
                      {showSummary && collapsed[row.teamId] && groupSpans[row.teamId] && (() => {
                        const sp = groupSpans[row.teamId];
                        const s = parseDate(sp.start);
                        const e = parseDate(sp.end);
                        if (!s || !e) return null;
                        const left = daysDiff(timelineStart, s) * dayWidth;
                        const width = Math.max((daysDiff(s, e) + 1) * dayWidth, 8);
                        return (
                          <div
                            className="absolute"
                            style={{ left, width, top: '50%', transform: 'translateY(-50%)', height: 12, zIndex: 6 }}
                            onMouseEnter={(ev) => setSpanTooltip({ start: sp.start, end: sp.end, total: sp.total, x: ev.clientX + 12, y: ev.clientY - 10 })}
                            onMouseMove={(ev) => setSpanTooltip((prev) => (prev ? { ...prev, x: ev.clientX + 12, y: ev.clientY - 10 } : prev))}
                            onMouseLeave={() => setSpanTooltip(null)}
                          >
                            {/* thin rollup bar */}
                            <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-1 rounded-full bg-gray-400/90 dark:bg-gray-400/80" />
                            {/* end caps */}
                            <div className="absolute left-0 top-0 h-full w-[3px] rounded-sm bg-gray-500 dark:bg-gray-300" />
                            <div className="absolute right-0 top-0 h-full w-[3px] rounded-sm bg-gray-500 dark:bg-gray-300" />
                          </div>
                        );
                      })()}
                    </div>
                  );
                }
                const t = row.task;
                const isDragging = dragState && dragState.task.id === t.id;
                const bar = getBarStyle(t);
                const dragBar = getDragBar(t);
                const displayBar = dragBar || bar;
                // Per-task bar colour (Pete: team colour drives task bars). A
                // task's OWN team colour wins, so a chart with mixed teams (the
                // gag panel — one synthetic group, many teams) paints each bar
                // in its team's colour rather than one group colour. Team-less
                // tasks fall back to the STATUS colour when the caller opts in
                // (statusColorFallback — gag panel), else the group palette
                // colour (Teams callers → behaviour unchanged, since their
                // teamed tasks carry team_color == the group colour anyway).
                const color = t.team_color
                  || (statusColorFallback
                    ? (TASK_STATUS_COLORS_HEX[t.status] || '#6B7280')
                    : (teamColorMap[row.teamId]?.hex || '#6B7280'));

                return (
                  <div key={`t-${t.id}`} className="relative border-b border-gray-100 dark:border-gray-700" style={{ height: ROW_HEIGHT }}>
                    {displayBar && (
                      <div
                        className={`absolute top-2.5 rounded-md overflow-hidden text-white ${isDragging ? 'shadow-lg' : 'shadow-sm hover:shadow-md'}`}
                        style={{
                          left: displayBar.left, width: Math.max(displayBar.width, 8), height: BAR_HEIGHT,
                          backgroundColor: color, opacity: isDragging ? 0.7 : (confirmDialog?.task.id === t.id ? 0.3 : 1),
                          // `GANTT_DRAG_Z` (27): strictly below the page-scroll band's z-30.
                          zIndex: isDragging ? GANTT_DRAG_Z : 10,
                          cursor: canDrag ? GANTT_CURSOR.move : GANTT_CURSOR.open,
                        }}
                        data-pan-skip
                        {...(isDragging ? { [GANTT_DRAGGING_ATTR]: '' } : null)}
                        onClick={() => { if (Date.now() - dragEndTime.current < 500) return; if (!isDragging && onTaskClick) onTaskClick(t); }}
                        onMouseEnter={(e) => showTooltip(e, t)}
                        onMouseLeave={hideTooltip}
                        title={`${t.title} (${(isDragging ? dragState.currentStart : t.start_date) || '?'} → ${(isDragging ? dragState.currentDue : t.due_date) || '?'})`}>

                        {/* Bar label */}
                        {showAssignees ? (() => {
                          // Gag Gantt: bar shows TITLE + ASSIGNEE only (Pete —
                          // no description, no status icon). Priority on tight
                          // bars: keep the TITLE readable, the assignee gives
                          // up space first (flexShrink 100 vs 1 → it truncates,
                          // then drops, before the title truncates). Unassigned
                          // bars get a red warning marker instead — scannable
                          // without hovering, and shown even on the tiniest bar.
                          const info = assigneeInfo(t);
                          const unassigned = info.inline == null;
                          const showTitle = displayBar.width > 44;
                          const showAssignee = !unassigned && displayBar.width > 64;
                          // The left resize handle (w-3 = 12px) only exists on a
                          // multi-day, draggable, >=24px bar. When present, push
                          // the marker (and the title) past it so the triangle
                          // clears the grip; otherwise sit at the bar's edge so
                          // tiny single-day bars still show it.
                          const hasLeftHandle = canDrag && ganttTaskDragZones(t, displayBar.width).showBothHandles;
                          const markerLeft = hasLeftHandle ? 15 : 3;
                          const titlePadLeft = unassigned ? (hasLeftHandle ? 29 : 18) : 12;
                          return (
                            <>
                              {/* Red "not assigned" marker — pointer-events-none
                                  so the bar keeps drag/click + its hover tooltip
                                  (which reads "Not assigned") AND the left resize
                                  handle stays grabbable. White+dark halo keeps it
                                  legible over any status colour incl. red, in
                                  light and dark. Offset clears the left grip. */}
                              {unassigned && (
                                <span className="absolute z-20 pointer-events-none flex items-center" style={{ left: markerLeft, top: 0, height: '100%' }} aria-hidden="true">
                                  <UnassignedMarker />
                                </span>
                              )}
                              {showTitle && (
                                <div className="flex items-center h-full pointer-events-none select-none"
                                  style={{ paddingLeft: titlePadLeft, paddingRight: canDrag ? 12 : 8 }}>
                                  <span className="text-xs text-white font-medium truncate" style={{ minWidth: 0, flexShrink: 1, textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                                    {t.title}
                                  </span>
                                  {showAssignee && (
                                    // flexShrink 100 → truncates/drops before the title does
                                    <span className="text-[10px] text-white/85 truncate ml-1.5" style={{ minWidth: 0, flexShrink: 100, textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                                      · {info.inline}
                                    </span>
                                  )}
                                </div>
                              )}
                            </>
                          );
                        })() : (
                          /* Teams callers — ORIGINAL behaviour, untouched */
                          displayBar.width > 50 ? (
                            <span className="text-xs text-white font-medium truncate block pointer-events-none select-none whitespace-nowrap" style={{ lineHeight: `${BAR_HEIGHT}px`, paddingLeft: 14, paddingRight: 14, textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                              <span className="mr-1" style={{ color: TASK_STATUS_ICON_COLORS[t.status] }}>{TASK_STATUS_ICONS[t.status] || ''}</span>{t.title}{t.description ? ` — ${t.description}` : ''}
                            </span>
                          ) : displayBar.width >= 16 && (
                            <span className="text-[10px] pointer-events-none select-none flex items-center justify-center" style={{ lineHeight: `${BAR_HEIGHT}px`, textShadow: '0 1px 2px rgba(0,0,0,0.3)', color: TASK_STATUS_ICON_COLORS[t.status] || '#fff' }}>
                              {TASK_STATUS_ICONS[t.status] || ''}
                            </span>
                          )
                        )}

                        {/* Drag handles */}
                        {canDrag && (() => {
                          const { isSingleDay, showBothHandles } = ganttTaskDragZones(t, displayBar.width);
                          return (
                            <>
                              {/* Body drag zone for multi-day (center area between handles) */}
                              {showBothHandles && (
                                <div className="absolute top-0 h-full cursor-move z-0"
                                  style={{ left: 12, right: 12, width: 'auto' }}
                                  onMouseDown={(e) => handleDragStart(e, t, 'body')}
                                  onTouchStart={(e) => handleDragStart(e, t, 'body')} />
                              )}
                              {/* Left handle — only for multi-day tasks */}
                              {showBothHandles && (
                                <div className="absolute top-0 left-0 w-3 h-full cursor-col-resize group/lhandle flex items-center justify-center z-20"
                                  onMouseDown={(e) => handleDragStart(e, t, 'left')}
                                  onTouchStart={(e) => handleDragStart(e, t, 'left')}>
                                  <div className="w-1 h-3 rounded-sm bg-white/50 group-hover/lhandle:bg-white/90 transition-colors" />
                                </div>
                              )}
                              {/* Right handle — always show (extends due date, works for single-day) */}
                              <div className={`absolute top-0 right-0 h-full cursor-col-resize group/rhandle flex items-center justify-center z-20 ${isSingleDay ? 'w-1/2' : 'w-3'}`}
                                onMouseDown={(e) => handleDragStart(e, t, 'right')}
                                onTouchStart={(e) => handleDragStart(e, t, 'right')}>
                                <div className="w-1 h-3 rounded-sm bg-white/50 group-hover/rhandle:bg-white/90 transition-colors" />
                              </div>
                              {/* Left half = body drag for single-day tasks (moves the whole task) */}
                              {isSingleDay && (
                                <div className="absolute top-0 left-0 w-1/2 h-full cursor-move z-10"
                                  onMouseDown={(e) => handleDragStart(e, t, 'body')}
                                  onTouchStart={(e) => handleDragStart(e, t, 'body')} />
                              )}
                            </>
                          );
                        })()}
                      </div>
                    )}

                    {/* Ghost bar preview — shows new position while confirm dialog is open */}
                    {confirmDialog && confirmDialog.task.id === t.id && (() => {
                      const ghostStart = parseDate(confirmDialog.newStart) || parseDate(confirmDialog.newDue);
                      const ghostEnd = parseDate(confirmDialog.newDue) || parseDate(confirmDialog.newStart);
                      if (!ghostStart || !ghostEnd) return null;
                      const ghostLeft = daysDiff(timelineStart, ghostStart) * dayWidth;
                      const ghostWidth = Math.max((daysDiff(ghostStart, ghostEnd) + 1) * dayWidth, 8);
                      return (
                        <div
                          className="absolute top-2.5 rounded-md border-2 border-dashed pointer-events-none shadow-sm"
                          style={{
                            left: ghostLeft, width: ghostWidth, height: BAR_HEIGHT,
                            backgroundColor: color, opacity: 0.85,
                            borderColor: '#fff',
                            zIndex: 15,
                          }}
                        />
                      );
                    })()}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* 📜 `pageScroll`'s sticky horizontal bar — only while the timeline
          overflows; `marginLeft: LABEL_WIDTH` makes its range the timeline's. */}
      {pageScroll && timelineOverflows ? (
        <StickyScrollbar
          ref={stickyBarRef}
          contentStyle={{ width: timelineScrollWidth }}
          testId="cross-team-gantt-scrollbar"
          style={{ marginLeft: LABEL_WIDTH }}
        />
      ) : null}

      {/* Live dates while a bar or milestone is dragged (`gantt/GanttDragDatesTooltip`). */}
      <GanttDragDatesTooltip
        containerRef={timelineRef}
        getMinTop={getPinnedBottom}
        testId="cross-team-gantt-drag-dates"
        drag={dragState
          ? { title: dragState.task.title, start: dragState.currentStart, due: dragState.currentDue, oldStart: dragState.originalStart, oldDue: dragState.originalDue }
          : msDrag ? { title: msDrag.milestone.label, date: msDrag.currentDate, oldDate: msDrag.milestone.date } : null}
      />

      {/* Hover tooltip */}
      {tooltip && !dragState && (
        <GanttBarTooltip
          tooltip={tooltip}
          getMinTop={getPinnedBottom}
          className="px-4 py-3"
          maxWidth={320}
          testId="cross-team-gantt-tooltip"
          onMouseEnter={cancelHideTooltip}
          onMouseLeave={hideTooltip}
        >
          <div className="font-semibold text-sm mb-1.5">{tooltip.task.title}</div>
          {tooltip.task.team_name && (
            <div className="flex items-center gap-1.5 mb-1.5">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: tooltip.task.team_color || '#3B82F6' }} />
              <span className="text-gray-600 dark:text-gray-300">{tooltip.task.team_name}</span>
            </div>
          )}
          <div className="flex items-center gap-1.5 mb-2">
            <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ backgroundColor: (TASK_STATUS_COLORS_HEX[tooltip.task.status] || '#6B7280') + '40' }}>
              {TASK_STATUS_LABELS[tooltip.task.status] || tooltip.task.status}
            </span>
            <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-white/15 capitalize">{tooltip.task.priority}</span>
          </div>
          {(tooltip.task.start_date || tooltip.task.due_date) && (
            <div className="text-gray-300 mb-1.5">
              {tooltip.task.start_date && <><span className="text-gray-500">Start</span> {formatShort(tooltip.task.start_date)}</>}
              {tooltip.task.start_date && tooltip.task.due_date && <span className="text-gray-500 mx-1">→</span>}
              {tooltip.task.due_date && <><span className="text-gray-500">Due</span> {formatShort(tooltip.task.due_date)}</>}
            </div>
          )}
          {tooltip.task.estimated_days && <div className="text-gray-400 mb-1.5">Est: {tooltip.task.estimated_days}d</div>}
          {/* Assignee line (gag Gantt only — prop-guarded so teams tooltips
              are unchanged). "Assigned: A, B (+N)" or "Not assigned". */}
          {showAssignees && (
            <div className="text-gray-300 mb-1.5">{assigneeInfo(tooltip.task).tooltipLine}</div>
          )}
          {tooltip.task.description && <div className="text-gray-400 mb-2 line-clamp-2">{tooltip.task.description}</div>}
          <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-700">
            {onViewDetails && (
              <button
                onClick={() => { setTooltip(null); onViewDetails(tooltip.task); }}
                className="px-2.5 py-1 rounded bg-gray-700 hover:bg-gray-600 text-white text-[10px] font-semibold transition-colors"
              >
                View Details
              </button>
            )}
            {onEditTask && (
              <button
                onClick={() => { setTooltip(null); onEditTask(tooltip.task); }}
                className="px-2.5 py-1 rounded bg-blue-600 hover:bg-blue-700 text-white text-[10px] font-semibold transition-colors"
              >
                Edit Task
              </button>
            )}
            {onDeleteTask && (
              <button
                onClick={() => { if (window.confirm(`Delete "${tooltip.task.title}"?`)) { setTooltip(null); onDeleteTask(tooltip.task); } }}
                className="px-2.5 py-1 rounded bg-red-600 hover:bg-red-700 text-white text-[10px] font-semibold transition-colors"
              >
                Delete
              </button>
            )}
          </div>
        </GanttBarTooltip>
      )}

      {/* Milestone hover tooltip — same visual language as the task tooltip */}
      {msTooltip && !msDrag && !dragState && outOfChart(
        <div
          className="fixed z-50 bg-gray-900 text-white text-xs rounded-lg shadow-xl px-3 py-2 pointer-events-none"
          style={{ left: msTooltip.x, top: msTooltip.y }}
        >
          <span className="font-semibold">{msTooltip.label}</span>
          <span className="text-gray-300 ml-2">{formatDate(msTooltip.date)}</span>
        </div>
      )}

      {/* Collapsed-group rollup-bar hover tooltip — same pill as the milestone one */}
      {spanTooltip && !dragState && !msDrag && outOfChart(
        <div
          className="fixed z-50 bg-gray-900 text-white text-xs rounded-lg shadow-xl px-3 py-2 pointer-events-none"
          style={{ left: spanTooltip.x, top: spanTooltip.y }}
        >
          <span className="font-semibold">Span:</span>
          <span className="text-gray-300 ml-1">{formatDate(spanTooltip.start)} – {formatDate(spanTooltip.end)}</span>
          <span className="text-gray-400 ml-2">· {spanTooltip.total} {spanTooltip.total === 1 ? 'task' : 'tasks'}</span>
        </div>
      )}

      {/* Milestone confirm dialog — same pattern as the task reschedule dialog */}
      {msConfirm && outOfChart(
        <div className={`fixed inset-0 z-50 flex items-center justify-center bg-black/50 ${MODAL_OVERLAY}`} onClick={() => setMsConfirm(null)}>
          <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-sm mx-4 p-6 ${MODAL_PANEL_MOBILE_CAP}`} onClick={e => e.stopPropagation()}>
            <h3 className="text-sm font-bold text-gray-800 dark:text-gray-200 mb-3">Move Milestone</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
              Move <strong>{msConfirm.milestone.label}</strong> to <span className="text-blue-600 dark:text-blue-400 font-semibold">{formatDate(msConfirm.newDate)}</span>?
            </p>
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-4">
              <span className="line-through text-gray-400 dark:text-gray-500">{formatDate(msConfirm.oldDate)}</span>
              {' '}&rarr;{' '}
              <span className="text-blue-600 dark:text-blue-400 font-semibold">{formatDate(msConfirm.newDate)}</span>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setMsConfirm(null)} className={BTN_XS.secondary}>Cancel</button>
              <button onClick={handleMsConfirm} disabled={msConfirmLoading} className={BTN_XS.confirm}>{msConfirmLoading ? 'Saving...' : 'Confirm'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm dialog */}
      {confirmDialog && outOfChart(
        <div className={`fixed inset-0 z-50 flex items-center justify-center bg-black/50 ${MODAL_OVERLAY}`} onClick={() => setConfirmDialog(null)}>
          <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-sm mx-4 p-6 ${MODAL_PANEL_MOBILE_CAP}`} onClick={e => e.stopPropagation()}>
            <h3 className="text-sm font-bold text-gray-800 dark:text-gray-200 mb-3">Reschedule Task</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1"><strong>{confirmDialog.task.title}</strong></p>
            <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1 mb-4">
              {confirmDialog.oldStart !== confirmDialog.newStart && (
                <div>Start: <span className="line-through text-gray-400">{formatShort(confirmDialog.oldStart)}</span> → <span className="text-blue-600 dark:text-blue-400 font-semibold">{formatShort(confirmDialog.newStart)}</span></div>
              )}
              {confirmDialog.oldDue !== confirmDialog.newDue && (
                <div>Due: <span className="line-through text-gray-400">{formatShort(confirmDialog.oldDue)}</span> → <span className="text-blue-600 dark:text-blue-400 font-semibold">{formatShort(confirmDialog.newDue)}</span></div>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmDialog(null)} className={BTN_XS.secondary}>Cancel</button>
              <button onClick={handleConfirm} disabled={confirmLoading} className={BTN_XS.confirm}>{confirmLoading ? 'Saving...' : 'Confirm'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
